import { CONTENT_PLATFORM_PROPERTY_NAME, contentIsPublished, contentIsPublishedWithScheduledUpdate } from 'content-management-data-lib';
import { registerQuery, useQuery } from 'data-fetching-client';
import { createContext, useCallback, useMemo, useState } from 'react';
import http from 'hub-http/clients/apiClient';
import { PublishActionTypes } from './types/publishButtonTypes';
import { jsx as _jsx } from "react/jsx-runtime";
const ContentCrmObjectQuery = registerQuery({
  fieldName: 'contentCrmObject',
  args: ['contentObjectType', 'contentCrmObjectId'],
  fetcher: ({
    contentObjectType,
    contentCrmObjectId
  }) => {
    return http.get(`/content-management/search/v1/${contentObjectType}/${contentCrmObjectId}`);
  }
});
export const ContentCrmObjectContext = /*#__PURE__*/createContext(undefined);
const ContentCrmObjectProvider = ({
  contentObjectType,
  contentCrmObjectId,
  children
}) => {
  const [contentCrmObject, setContentCrmObject] = useState(undefined);
  const {
    loading,
    error
  } = useQuery(ContentCrmObjectQuery, {
    variables: {
      contentObjectType,
      contentCrmObjectId
    },
    onCompleted: response => {
      setContentCrmObject(response.contentCrmObject);
    }
  });
  const reconcileContentCrmObjectForPublishActions = useCallback((actionType, scheduledDates) => {
    if (!contentCrmObject) {
      return;
    }
    switch (actionType) {
      case PublishActionTypes.PUBLISH:
        {
          const publishedAt = Date.now();
          setContentCrmObject(prevContentCrmObject => {
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.PUBLISHED_AT]: {
                  value: `${publishedAt}`,
                  rawValue: publishedAt
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE]: {
                  value: 'PUBLISHED',
                  rawValue: 'PUBLISHED'
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.STATE]: {
                  value: 'PUBLISHED',
                  rawValue: 'PUBLISHED'
                }
              })
            });
          });
          break;
        }
      case PublishActionTypes.UNPUBLISH:
        {
          setContentCrmObject(prevContentCrmObject => {
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.PUBLISHED_AT]: {
                  value: null,
                  rawValue: null
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE]: {
                  value: 'DRAFT',
                  rawValue: 'DRAFT'
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.STATE]: {
                  value: 'DRAFT',
                  rawValue: 'DRAFT'
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE]: {
                  value: null,
                  rawValue: null
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE]: {
                  value: null,
                  rawValue: null
                }
              })
            });
          });
          break;
        }
      case PublishActionTypes.SCHEDULE_PUBLISH:
        {
          setContentCrmObject(prevContentCrmObject => {
            const isPublished = contentIsPublished(prevContentCrmObject);
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE]: {
                  value: isPublished ? 'SCHEDULED_UPDATE' : 'SCHEDULED',
                  rawValue: isPublished ? 'SCHEDULED_UPDATE' : 'SCHEDULED'
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE]: {
                  value: `${scheduledDates.schedulePublishDate}`,
                  rawValue: scheduledDates.schedulePublishDate
                }
              })
            });
          });
          break;
        }
      case PublishActionTypes.SCHEDULE_UNPUBLISH:
        {
          setContentCrmObject(prevContentCrmObject => {
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE]: {
                  value: `${scheduledDates.scheduleUnpublishDate}`,
                  rawValue: scheduledDates.scheduleUnpublishDate
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL]: scheduledDates.unpublishExpiryUrl ? {
                  value: scheduledDates.unpublishExpiryUrl,
                  rawValue: scheduledDates.unpublishExpiryUrl
                } : prevContentCrmObject.properties[CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL]
              })
            });
          });
          break;
        }
      case PublishActionTypes.CANCEL_SCHEDULED_PUBLISH:
        {
          setContentCrmObject(prevContentCrmObject => {
            const isCancelingScheduledUpdate = contentIsPublishedWithScheduledUpdate(prevContentCrmObject);
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE]: {
                  value: isCancelingScheduledUpdate ? 'PUBLISHED' : 'DRAFT',
                  rawValue: isCancelingScheduledUpdate ? 'PUBLISHED' : 'DRAFT'
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE]: {
                  value: null,
                  rawValue: null
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE]: {
                  value: null,
                  rawValue: null
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL]: {
                  value: null,
                  rawValue: null
                }
              })
            });
          });
          break;
        }
      case PublishActionTypes.CANCEL_SCHEDULED_UNPUBLISH:
        {
          setContentCrmObject(prevContentCrmObject => {
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE]: {
                  value: null,
                  rawValue: null
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL]: {
                  value: null,
                  rawValue: null
                }
              })
            });
          });
          break;
        }
      default:
        break;
    }
  }, [contentCrmObject]);
  const value = useMemo(() => ({
    contentCrmObject,
    contentCrmObjectLoading: loading,
    reconcileContentCrmObjectForPublishActions,
    contentCrmObjectId,
    contentObjectType,
    contentCrmObjectError: error
  }), [contentCrmObject, contentCrmObjectId, contentObjectType, error, loading, reconcileContentCrmObjectForPublishActions]);
  return /*#__PURE__*/_jsx(ContentCrmObjectContext.Provider, {
    value: value,
    children: children
  });
};
export default ContentCrmObjectProvider;